import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { analyticsAPI, trainerAPI } from '../../api';
import { cn } from '../../utils';
import { Card, Badge, Spinner } from '../../components/ui';
import { 
  ArrowLeft, Download, Calendar, Star, Trophy, Target, 
  TrendingUp, Activity, Award, Dumbbell, Scale, Clock
} from 'lucide-react';

// Progress bar component
const ProgressBar = ({ value, max, color = "primary" }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const colorClasses = {
    primary: "bg-primary-500",
    blue: "bg-blue-500",
    amber: "bg-amber-500",
    green: "bg-green-500"
  };
  
  return (
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
      <div 
        className={cn("h-full rounded-full transition-all duration-500", colorClasses[color])}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

// Stat card for the report
const ReportStatCard = ({ icon: Icon, label, value, subvalue, color = "primary" }) => {
  const colorClasses = {
    primary: "bg-primary-50 text-primary-600",
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    green: "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600"
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow">
      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-3", colorClasses[color])}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subvalue && <p className="text-xs text-gray-400 mt-1">{subvalue}</p>}
    </div>
  );
};

export const ClientProgressReportPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [days, setDays] = useState(30);

  useEffect(() => {
    const loadReport = async () => {
      setLoading(true);
      try {
        const response = await analyticsAPI.getClientReport(clientId, days);
        setReport(response.data);
      } catch (err) {
        console.error('Failed to load client report:', err);
      } finally {
        setLoading(false);
      }
    };
    loadReport();
  }, [clientId, days]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await analyticsAPI.exportClientReportCSV(clientId, days);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `client_report_${report?.client_name?.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-12">
        <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Report Not Found</h3>
        <p className="text-gray-500 mt-1">Unable to load client progress report</p>
        <button 
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 text-primary-600 hover:text-primary-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-progress-report">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-2 text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Progress Report</h1>
          <p className="text-gray-500 mt-1">{report.client_name} &bull; {report.client_email}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={e => setDays(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <button
            onClick={handleExportCSV}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <ReportStatCard 
          icon={Calendar} 
          label="Total Sessions" 
          value={report.total_sessions}
          subvalue={`${report.confirmed_sessions} confirmed`}
          color="primary"
        />
        <ReportStatCard 
          icon={Target} 
          label="Attendance Rate" 
          value={`${report.attendance_rate}%`}
          color="green"
        />
        <ReportStatCard 
          icon={Star} 
          label="Total Points" 
          value={report.total_points?.toLocaleString()}
          subvalue={`+${report.points_this_period} this period`}
          color="amber"
        />
        <ReportStatCard 
          icon={TrendingUp} 
          label="Current Streak" 
          value={`${report.current_streak} days`}
          subvalue={`Max: ${report.max_streak} days`}
          color="blue"
        />
        <ReportStatCard 
          icon={Trophy} 
          label="Level" 
          value={report.level}
          color="purple"
        />
        <ReportStatCard 
          icon={Award} 
          label="Badges Earned" 
          value={report.badges_earned}
          color="amber"
        />
      </div>

      {/* Progress Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Progress */}
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Session Progress</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Attendance Rate</span>
                <span className="font-medium text-gray-900">{report.attendance_rate}%</span>
              </div>
              <ProgressBar value={report.attendance_rate} max={100} color="green" />
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
              <div className="text-center">
                <p className="text-3xl font-bold text-primary-600">{report.confirmed_sessions}</p>
                <p className="text-sm text-gray-500">Confirmed</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-400">{report.total_sessions - report.confirmed_sessions}</p>
                <p className="text-sm text-gray-500">Pending/Missed</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Achievements */}
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Achievements</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-3 bg-amber-50 rounded-lg">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Trophy className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Level {report.level}</p>
                <p className="text-sm text-gray-500">{report.total_points?.toLocaleString()} total points</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-gray-50 rounded-lg">
                <Award className="w-6 h-6 text-purple-500 mx-auto mb-1" />
                <p className="font-bold text-gray-900">{report.badges_earned}</p>
                <p className="text-xs text-gray-500">Badges</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <Target className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                <p className="font-bold text-gray-900">{report.challenges_completed}</p>
                <p className="text-xs text-gray-500">Challenges</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <Dumbbell className="w-6 h-6 text-green-500 mx-auto mb-1" />
                <p className="font-bold text-gray-900">{report.personal_records}</p>
                <p className="text-xs text-gray-500">PRs</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Activity Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Exercises */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Dumbbell className="w-5 h-5 text-primary-500" />
            <h3 className="font-semibold text-gray-900">Top Exercises</h3>
          </div>
          {report.top_exercises?.length > 0 ? (
            <div className="space-y-3">
              {report.top_exercises.map((exercise, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
                    idx === 0 ? "bg-primary-100 text-primary-700" : "bg-gray-100 text-gray-600"
                  )}>
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{exercise.name}</p>
                  </div>
                  <div className="text-sm text-gray-500">
                    {exercise.sets} sets
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400">
              <Dumbbell className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No exercise data yet</p>
            </div>
          )}
        </Card>

        {/* Weekly Summary */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-blue-500" />
            <h3 className="font-semibold text-gray-900">Weekly Summary</h3>
          </div>
          {report.weekly_summary?.length > 0 ? (
            <div className="space-y-3">
              {report.weekly_summary.slice(-6).map((week, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className="w-20 text-sm text-gray-500">{week.week}</div>
                  <div className="flex-1">
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${(week.confirmed / Math.max(week.sessions, 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium text-gray-900">{week.confirmed}</span>
                    <span className="text-gray-400">/{week.sessions}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400">
              <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No weekly data yet</p>
            </div>
          )}
        </Card>
      </div>

      {/* Additional Stats */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Additional Metrics</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <Scale className="w-6 h-6 text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{report.body_metrics_entries}</p>
            <p className="text-sm text-gray-500">Body Metrics Logged</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <TrendingUp className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{report.max_streak}</p>
            <p className="text-sm text-gray-500">Best Streak (days)</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <Star className="w-6 h-6 text-amber-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{report.points_this_period}</p>
            <p className="text-sm text-gray-500">Points This Period</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <Clock className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{report.current_streak}</p>
            <p className="text-sm text-gray-500">Current Streak</p>
          </div>
        </div>
      </Card>

      {/* Report Footer */}
      <div className="text-center text-sm text-gray-400 py-4">
        Report Period: {report.report_period} &bull; Generated on {new Date().toLocaleDateString()}
      </div>
    </div>
  );
};

export default ClientProgressReportPage;
